import { readPremiumRefusal } from '../../../@commons/@services/front/premium-error.util';
import { PremiumTeaserComponent } from '../../../@commons/ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../../../@commons/@toasts/premium-toast/premium-toast.component';
import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { HeaderPaddingSafeAreaComponent } from '../../../@commons/@components/header-padding-safe-area/header-padding-safe-area.component';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { StorageService } from '../../../@commons/@services/front/storage.service';
import { GardenBedApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/garden-bed/garden-bed-api';
import { LocationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/location-api';
import { GardenBedType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/garden-bed-type.enum';
import { SunchineLevels } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/sunchine-levels.enum';
import { Orientation } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/orientation.enum';
import { SoilType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/soil-type.enum';
import { ChoiceOption } from '../../../@commons/ui-components/choice-selector/choice-selector.component';

interface Option {
  label: string;
  value: string;
}

@Component({
  selector: 'vw-garden-bed-create',
  imports: [
    PremiumTeaserComponent,
    PremiumToastComponent,
    SharedModule,
    FormsModule,
    TranslatePipe,
    HeaderPaddingSafeAreaComponent
],
  templateUrl: './garden-bed-create.component.html',
  styleUrl: './garden-bed-create.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: GardenBedApi, useClass: GardenBedApi },
    { provide: LocationApi, useClass: LocationApi },
  ],
})
export class GardenBedCreateComponent implements OnInit {
  readonly GardenBedType = GardenBedType;

  // Réutilisation inline (ex. wizard de création de zone) : zone imposée + pas de navigation.
  @Input() locationUid?: string;
  @Input() autoNavigate = true;
  @Output() bedCreated = new EventEmitter<any>();

  // Form state
  name = '';
  type: GardenBedType = GardenBedType.RECTANGULAR;
  rows = 3;
  columns = 4;
  capacity = 3;
  exposure?: SunchineLevels;
  orientation?: Orientation;
  soilType?: SoilType;

  // Zone : soit imposée (header) si passée en query param, soit choisie (dropdown).
  zone?: Option;
  zones: Option[] = [];
  presetZoneName?: string;
  private presetZoneUid?: string;

  // Pills (réutilisent le composant générique vw-choice-selector)
  orientationPills: ChoiceOption[] = [];
  exposurePills: ChoiceOption[] = [];
  soilPills: ChoiceOption[] = [];

  submitLoading = false;
  nameError = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private gardenBedApi: GardenBedApi,
    private locationApi: LocationApi,
  ) {}

  async ngOnInit(): Promise<void> {
    this.orientationPills = Object.values(Orientation).map((v) => ({
      value: v,
      label: `garden.beds.enums.orientation.${v}`,
    }));
    this.exposurePills = Object.values(SunchineLevels).map((v) => ({
      value: v,
      label: `garden.beds.enums.exposure.${v}`,
    }));
    this.soilPills = Object.values(SoilType).map((v) => ({
      value: v,
      label: `garden.beds.enums.soil.${v}`,
    }));
    // L'@Input a priorité (usage inline), le query param reste le fallback (route /garden/beds/create).
    this.presetZoneUid =
      this.locationUid ??
      this.route.snapshot.queryParamMap.get('locationUid') ??
      undefined;
    await this.loadZones();
  }

  async loadZones(): Promise<void> {
    const addressUid = this.storageService.getItem('address-uid') ?? undefined;
    const res = await this.locationApi.list(
      { addressUid } as any,
      0,
      50,
      this.storageService.getCurrentLanguage(),
    );
    this.zones = res.results.map((l: any) => ({ label: l.name, value: l.uid }));
    if (this.presetZoneUid) {
      this.zone = this.zones.find((z) => z.value === this.presetZoneUid);
      this.presetZoneName = this.zone?.label;
    }
  }

  get isRectangular(): boolean {
    return this.type === GardenBedType.RECTANGULAR;
  }

  selectType(type: GardenBedType): void {
    this.type = type;
  }

  // --- Steppers
  step(field: 'rows' | 'columns' | 'capacity', delta: number): void {
    const max = field === 'capacity' ? 50 : 20;
    this[field] = Math.min(max, Math.max(1, (this[field] ?? 1) + delta));
  }

  get gridCellCount(): number {
    return (this.rows || 0) * (this.columns || 0);
  }

  // Tableau pour l'aperçu (rect = rows*columns, pot = capacity).
  get previewCells(): number[] {
    const n = this.isRectangular ? this.gridCellCount : this.capacity || 0;
    return Array.from({ length: Math.min(n, 60) });
  }

  get canSubmit(): boolean {
    return !!this.name?.trim() && (!!this.zone || !!this.presetZoneUid);
  }

  async submit(): Promise<void> {
    this.nameError = !this.name?.trim();
    if (!this.canSubmit) {
      return;
    }
    this.submitLoading = true;
    try {
      const created = await this.gardenBedApi.create({
        locationUid: this.presetZoneUid ?? this.zone!.value,
        name: this.name.trim(),
        type: this.type,
        exposure: this.exposure,
        orientation: this.orientation,
        soilType: this.soilType,
        rows: this.isRectangular ? Number(this.rows) : undefined,
        columns: this.isRectangular ? Number(this.columns) : undefined,
        capacity: this.isRectangular ? undefined : Number(this.capacity),
      });
      this.bedCreated.emit(created);
      if (this.autoNavigate) {
        this.router.navigate(['/garden/beds', created.uid]);
      }
    } catch (error) {
      // Un refus premium n'est jamais une erreur rouge : on explique et on
      // propose. Toute autre erreur suit le chemin habituel.
      const refusal = readPremiumRefusal(error);
      if (!refusal) throw error;
      this.premiumMessageKey = refusal.messageKey ?? 'premium.gate.bed';
    } finally {
      this.submitLoading = false;
    }
  }

  /** Message d'invitation premium, quand la limite gratuite est atteinte. */
  premiumMessageKey: string | null = null;
  premiumPaywallOpen = false;

  openPaywall(): void {
    this.premiumPaywallOpen = true;
  }

  back(): void {
    this.router.navigate(['/garden/list']);
  }
}
